<template>
    <v-form ref="form" autocomplete="off" @submit.prevent="save">
        <v-row>
            <v-col cols="12" sm="6">
                <v-text-field
                    v-model="form.nombre"
                    label="Nombre del plan"
                    prepend-icon="mdi-package-variant"
                    :rules="required"
                    :error="hasInputErrors('nombre')"
                    :error-messages="getInputErrors('nombre')"
                    color="secondary"
                />
            </v-col>
            <v-col cols="12" sm="5">
                <v-text-field
                    v-model="form.descripcion"
                    label="Descripción"
                    prepend-icon="mdi-text"
                    color="secondary"
                />
            </v-col>
            <v-col cols="12" sm="1" class="d-flex align-center">
                <v-switch v-model="form.active" label="Activo" color="primary" />
            </v-col>
        </v-row>

        <v-divider class="my-4" />
        <div class="subtitle-1 mb-3">
            <v-icon left>mdi-shield-account</v-icon>
            Roles del plan
        </div>

        <div v-if="loadingPermissions" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
            <div class="mt-2 caption grey--text">Cargando permisos...</div>
        </div>

        <v-expansion-panels v-else multiple>
            <v-expansion-panel
                v-for="(template, idx) in form.roleTemplates"
                :key="idx"
            >
                <v-expansion-panel-header>
                    <div class="d-flex align-center">
                        <v-icon left small>mdi-account-key</v-icon>
                        <strong class="text-capitalize">{{ template.roleName }}</strong>
                        <v-chip small class="ml-3" color="primary" outlined>
                            {{ template.permissions.length }} / {{ availablePermissions.length }} permisos
                        </v-chip>
                    </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <!-- Acciones globales del rol -->
                    <v-row class="mb-2">
                        <v-col cols="12" class="d-flex align-center py-1">
                            <v-btn x-small text color="primary" @click="selectAll(template)">
                                <v-icon x-small left>mdi-check-all</v-icon>Seleccionar todos
                            </v-btn>
                            <v-btn x-small text color="grey darken-1" @click="clearAll(template)" class="ml-2">
                                <v-icon x-small left>mdi-close</v-icon>Limpiar
                            </v-btn>
                        </v-col>
                    </v-row>

                    <!-- Secciones de permisos agrupados -->
                    <v-expansion-panels flat multiple accordion>
                        <v-expansion-panel
                            v-for="(group, groupKey) in permissionGroups"
                            :key="groupKey"
                        >
                            <v-expansion-panel-header class="py-1 px-2" style="min-height:36px">
                                <div class="d-flex align-center">
                                    <v-icon x-small left :color="groupColor(groupKey)">{{ groupIcon(groupKey) }}</v-icon>
                                    <span class="caption font-weight-bold">{{ groupLabel(groupKey) }}</span>
                                    <v-chip x-small class="ml-2" :color="groupSelectedCount(template, group) === group.length ? 'success' : 'grey lighten-2'">
                                        {{ groupSelectedCount(template, group) }}/{{ group.length }}
                                    </v-chip>
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content class="pa-0">
                                <v-row class="px-2 py-1">
                                    <v-col cols="12" class="py-0 d-flex">
                                        <v-btn x-small text color="primary" @click="selectGroup(template, group)">todos</v-btn>
                                        <v-btn x-small text color="grey" @click="clearGroup(template, group)">ninguno</v-btn>
                                    </v-col>
                                    <v-col
                                        v-for="perm in group"
                                        :key="perm"
                                        cols="12" sm="6" md="4"
                                        class="py-0"
                                    >
                                        <v-checkbox
                                            v-model="template.permissions"
                                            :value="perm"
                                            :label="permLabel(perm)"
                                            dense
                                            hide-details
                                            class="mt-1"
                                        />
                                    </v-col>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-form>
</template>

<script>
import { InputErrorsByProps, RequiredRule } from '@dracul/common-frontend'
import InviraRoleProvider from '../../../providers/InviraRoleProvider'

const DEFAULT_ROLE_NAMES = ['supervisor', 'vendedor', 'operator']

// Mapeo de prefijo → etiqueta legible
const GROUP_LABELS = {
    BASE: 'Base',
    SECURITY: 'Seguridad',
    NOTIFICATION: 'Notificaciones',
    CUSTOMIZATION: 'Personalización',
    FILE: 'Archivos',
    DASHBOARD: 'Dashboard',
    MARCA: 'Marcas',
    MODELO: 'Modelos',
    TAMANIO: 'Tamaños',
    PROVEEDOR: 'Proveedores',
    LISTA: 'Lista de precios',
    PRODUCTO: 'Productos',
    MOVIMIENTO: 'Movimientos',
    DEPOSITO: 'Depósitos',
    RECARGO: 'Recargos',
    OFERTA: 'Ofertas',
    COMPANY: 'Empresa',
    USER: 'Configuración usuario',
    CLIENTE: 'Clientes',
    FACTURACION: 'Facturación',
    REGISTRO: 'Registro de actividades',
    VENTAS: 'Ventas',
    VENDEDOR: 'Vendedores',
    COMPRAS: 'Compras',
    PLAN: 'Planes',
}

const GROUP_ICONS = {
    BASE: 'mdi-home',
    SECURITY: 'mdi-shield-lock',
    NOTIFICATION: 'mdi-bell',
    CUSTOMIZATION: 'mdi-palette',
    FILE: 'mdi-file',
    DASHBOARD: 'mdi-view-dashboard',
    MARCA: 'mdi-tag',
    MODELO: 'mdi-shape',
    TAMANIO: 'mdi-resize',
    PROVEEDOR: 'mdi-truck',
    LISTA: 'mdi-format-list-bulleted',
    PRODUCTO: 'mdi-package-variant-closed',
    MOVIMIENTO: 'mdi-swap-horizontal',
    DEPOSITO: 'mdi-warehouse',
    RECARGO: 'mdi-percent',
    OFERTA: 'mdi-sale',
    COMPANY: 'mdi-domain',
    USER: 'mdi-account-cog',
    CLIENTE: 'mdi-account-group',
    FACTURACION: 'mdi-receipt',
    REGISTRO: 'mdi-history',
    VENTAS: 'mdi-cart',
    VENDEDOR: 'mdi-account-tie',
    COMPRAS: 'mdi-shopping',
    PLAN: 'mdi-clipboard-list',
}

const GROUP_COLORS = {
    DASHBOARD: 'blue',
    PRODUCTO: 'green',
    VENTAS: 'orange',
    COMPRAS: 'purple',
    FACTURACION: 'red',
    CLIENTE: 'teal',
    VENDEDOR: 'indigo',
}

// Orden de visualización de los grupos
const GROUP_ORDER = [
    'DASHBOARD', 'PRODUCTO', 'MARCA', 'MODELO', 'TAMANIO',
    'DEPOSITO', 'MOVIMIENTO', 'RECARGO', 'OFERTA', 'LISTA',
    'CLIENTE', 'VENTAS', 'VENDEDOR', 'COMPRAS',
    'FACTURACION', 'REGISTRO', 'PROVEEDOR',
    'COMPANY', 'USER', 'PLAN',
    'BASE', 'SECURITY', 'NOTIFICATION', 'CUSTOMIZATION', 'FILE',
]

function getPrefix(perm) {
    // VENTAS_ORDEN_SHOW → VENTAS, LISTA_PRECIOS_PROVEEDOR → LISTA
    return perm.split('_')[0]
}

export default {
    name: 'PlanForm',
    mixins: [InputErrorsByProps, RequiredRule],

    props: {
        value: { type: Object, required: true }
    },

    data() {
        return {
            availablePermissions: [],
            loadingPermissions: false
        }
    },

    computed: {
        form: {
            get() { return this.value },
            set(val) { this.$emit('input', val) }
        },

        permissionGroups() {
            // Agrupar permisos por prefijo
            const raw = {}
            this.availablePermissions.forEach(perm => {
                const prefix = getPrefix(perm)
                if (!raw[prefix]) raw[prefix] = []
                raw[prefix].push(perm)
            })

            // Ordenar grupos según GROUP_ORDER, resto al final
            const ordered = {}
            GROUP_ORDER.forEach(key => {
                if (raw[key]) ordered[key] = raw[key].sort()
            })
            Object.keys(raw).forEach(key => {
                if (!ordered[key]) ordered[key] = raw[key].sort()
            })
            return ordered
        }
    },

    created() {
        this.loadPermissions()
        if (!this.form.roleTemplates || this.form.roleTemplates.length === 0) {
            const templates = DEFAULT_ROLE_NAMES.map(roleName => ({
                roleName,
                permissions: [],
                childRoleNames: []
            }))
            this.$emit('input', { ...this.form, roleTemplates: templates })
        }
    },

    methods: {
        validate() {
            return this.$refs.form.validate()
        },
        loadPermissions() {
            this.loadingPermissions = true
            InviraRoleProvider.fetchRoles()
                .then(r => {
                    const allRoles = r.data.adminInviraRoleFetch || []
                    const permsSet = new Set()
                    allRoles.forEach(role => {
                        if (role.permissions) role.permissions.forEach(p => permsSet.add(p))
                    })
                    this.availablePermissions = Array.from(permsSet).sort()
                })
                .catch(err => console.error('[PlanForm] Error loading permissions:', err))
                .finally(() => this.loadingPermissions = false)
        },

        // Acciones globales del rol
        selectAll(template) {
            template.permissions = [...this.availablePermissions]
        },
        clearAll(template) {
            template.permissions = []
        },

        // Acciones por grupo
        selectGroup(template, group) {
            const set = new Set(template.permissions)
            group.forEach(p => set.add(p))
            template.permissions = Array.from(set)
        },
        clearGroup(template, group) {
            const groupSet = new Set(group)
            template.permissions = template.permissions.filter(p => !groupSet.has(p))
        },
        groupSelectedCount(template, group) {
            const permSet = new Set(template.permissions)
            return group.filter(p => permSet.has(p)).length
        },

        // Labels e íconos
        groupLabel(key) {
            return GROUP_LABELS[key] || key
        },
        groupIcon(key) {
            return GROUP_ICONS[key] || 'mdi-circle-small'
        },
        groupColor(key) {
            return GROUP_COLORS[key] || 'grey darken-1'
        },
        permLabel(perm) {
            // MARCA_CREATE → Create, VENTAS_ORDEN_SHOW → Orden Show
            const parts = perm.split('_')
            const rest = parts.slice(1).join(' ').toLowerCase()
            return rest.charAt(0).toUpperCase() + rest.slice(1)
        }
    }
}
</script>
